import { BellRing, Landmark, PiggyBank, Scale, Target, TriangleAlert } from "lucide-react"

import { monto as comoMonto } from "@/componentes/tesoreria/dinero"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"

/** Lo que estas tarjetas leen del estado del mes. Espejo de `EstadoPresupuestoOut`. */
export type TotalesDelMes = {
  tope: string
  ejecutado_dentro: string
  comprometido_dentro: string
  fuera_de_partida: string
  comprometido_fuera: string
  ordenes_fuera: number
  ejecutado_total: string
  saldo: string
  excedidas: number
  partidas: {
    id: number
    categoria: string
    porcentaje: number
    umbral: number
    alerta: "UMBRAL" | "EXCEDIDA" | null
  }[]
}

const negativo = (cifra: string) => cifra.trim().startsWith("-")

/**
 * Los totales del mes del presupuesto (pedido el 12/10): el tope, lo ejecutado DENTRO
 * de las partidas, lo ejecutado FUERA de ellas, su suma y el saldo.
 *
 * ⚠ **Las cifras son del servidor**; aquí no se suma nada. Los montos viajan como
 * cadena para no perder céntimos, y sumar en el navegador sería otra verdad.
 *
 * ⚠ **El saldo es el tope menos lo USADO dentro de las partidas** —ejecutado más
 * comprometido—, no menos lo ejecutado total: lo de fuera no tiene tope del que salir.
 */
export function TotalesPresupuesto({
  datos,
  mes,
  onVerPartidas,
  onVerFuera,
}: {
  datos: TotalesDelMes
  mes: string
  onVerPartidas: () => void
  onVerFuera: () => void
}) {
  const sinSaldo = negativo(datos.saldo)
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <TarjetaKpi
        titulo={`Presupuesto de ${mes}`}
        detalle="La suma de los topes vigentes"
        Icono={Target}
        color="var(--info)"
        cantidad={comoMonto(datos.tope)}
        pie={`${datos.partidas.length} ${datos.partidas.length === 1 ? "partida" : "partidas"} · en dólares · pulsa para verlas`}
        onClick={onVerPartidas}
      />
      <TarjetaKpi
        titulo="Ejecutado en partida"
        detalle="Lo pagado en categorías con tope"
        Icono={PiggyBank}
        color={datos.excedidas > 0 ? "var(--warning)" : "var(--success)"}
        cantidad={comoMonto(datos.ejecutado_dentro)}
        pie={`Y ${comoMonto(datos.comprometido_dentro)} comprometido · pulsa para ver las partidas`}
        onClick={onVerPartidas}
      />
      <TarjetaKpi
        titulo="Ejecutado fuera de partida"
        detalle="Lo pagado sin tope que lo gobierne"
        Icono={TriangleAlert}
        color={Number(datos.fuera_de_partida) > 0 ? "var(--destructive)" : "var(--fg-muted)"}
        cantidad={comoMonto(datos.fuera_de_partida)}
        pie={`${datos.ordenes_fuera} ${datos.ordenes_fuera === 1 ? "orden" : "órdenes"} · y ${comoMonto(datos.comprometido_fuera)} comprometido · pulsa para verlas`}
        onClick={onVerFuera}
      />
      <TarjetaKpi
        titulo="Total ejecutado"
        detalle="Dentro y fuera de partida"
        Icono={Landmark}
        color="var(--info)"
        cantidad={comoMonto(datos.ejecutado_total)}
        pie="Todo lo que salió del banco este mes"
      />
      <TarjetaKpi
        titulo={sinSaldo ? "Presupuesto pasado" : "Saldo del presupuesto"}
        detalle="Tope menos lo usado en partida"
        Icono={Scale}
        color={sinSaldo ? "var(--destructive)" : "var(--success)"}
        cantidad={comoMonto(datos.saldo)}
        pie="Usado = ejecutado + comprometido. Lo de fuera no resta: no tiene tope."
      />
    </div>
  )
}

/**
 * Las partidas en alerta, dichas arriba y con palabras (12/10). Cada una avisa además
 * por la campanita y Telegram una vez por nivel y mes; aquí se ve el estado de HOY.
 */
export function AlertasPresupuesto({ partidas }: { partidas: TotalesDelMes["partidas"] }) {
  const enAlerta = partidas.filter((p) => p.alerta !== null)
  if (enAlerta.length === 0) return null
  return (
    <div
      role="status"
      className="tarjeta flex flex-wrap items-start gap-2 p-3 text-sm"
      style={{ borderColor: "color-mix(in oklab, var(--warning) 45%, var(--border))" }}
    >
      <BellRing
        size={16}
        aria-hidden={true}
        className="mt-0.5 shrink-0"
        style={{ color: "var(--warning)" }}
      />
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {enAlerta.length} {enAlerta.length === 1 ? "partida en alerta" : "partidas en alerta"}
        </p>
        <ul className="mt-1 space-y-0.5 tabular-nums" style={{ color: "var(--fg-muted)" }}>
          {enAlerta.map((p) => (
            <li key={p.id}>
              {p.categoria}:{" "}
              {p.alerta === "EXCEDIDA"
                ? `excedida (${p.porcentaje} %)`
                : `${p.porcentaje} % usado, umbral ${p.umbral} %`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
